import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Cloud, Settings, Home, BarChart3, Shield, Zap } from 'lucide-react'

function Layout({ children }) {
  const location = useLocation()

  const isActive = (path) => location.pathname === path

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 via-white to-primary-50">
      {/* Header */}
      <header className="glass-effect border-b border-white/30 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Cloud className="h-8 w-8 text-primary-600" />
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-success-500 rounded-full shadow-[0_0_8px_rgba(34,197,94,0.4)]"></div>
              </div>
              <div>
                <h1 className="text-xl font-bold gradient-text">
                  CloudHelper Pro
                </h1>
                <p className="text-xs text-secondary-500 font-medium">Enterprise Infrastructure Platform</p>
              </div>
            </div>
            
            <nav className="flex items-center space-x-2">
              <Link
                to="/"
                className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                  isActive('/') 
                    ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-medium' 
                    : 'text-secondary-600 hover:text-secondary-900 hover:bg-white/60 hover:shadow-soft'
                }`}
              >
                <Home className="h-4 w-4" />
                <span>Configure</span>
              </Link>
              <Link
                to="/configurations"
                className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                  isActive('/configurations') 
                    ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-medium' 
                    : 'text-secondary-600 hover:text-secondary-900 hover:bg-white/60 hover:shadow-soft'
                }`}
              >
                <Settings className="h-4 w-4" />
                <span>Saved Configs</span>
              </Link>
              <div className="flex items-center space-x-3 ml-6 pl-6 border-l border-white/30">
                <div className="flex items-center space-x-1">
                  <div className="status-active status-indicator"></div>
                  <span className="text-xs text-secondary-500 font-medium">Live Pricing</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Shield className="h-4 w-4 text-success-600" />
                  <span className="text-xs text-secondary-500 font-medium">GDPR Ready</span>
                </div>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-fade-in">
          {children}
        </div>
      </main>

      {/* Footer */}
      <footer className="glass-effect border-t border-white/30 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start space-x-2 mb-3">
                <Cloud className="h-6 w-6 text-primary-600" />
                <span className="font-bold gradient-text">CloudHelper Pro</span>
              </div>
              <p className="text-sm text-secondary-600">
                Enterprise-grade cloud infrastructure planning with real-time pricing and compliance assurance.
              </p>
            </div>
            
            <div className="text-center">
              <h3 className="font-semibold text-secondary-900 mb-3">Supported Providers</h3>
              <div className="flex flex-wrap justify-center gap-2">
                <span className="tag tag-primary">AWS</span>
                <span className="tag tag-primary">GCP</span>
                <span className="tag tag-primary">Azure</span>
                <span className="tag tag-success">OTC</span>
                <span className="tag tag-success">OVHcloud</span>
                <span className="tag tag-success">IONOS</span>
              </div>
            </div>
            
            <div className="text-center md:text-right">
              <h3 className="font-semibold text-secondary-900 mb-3">Compliance & Security</h3>
              <div className="flex flex-wrap justify-center md:justify-end gap-2">
                <div className="flex items-center space-x-1">
                  <Shield className="h-4 w-4 text-success-600" />
                  <span className="text-sm text-secondary-600">GDPR Compliant</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Zap className="h-4 w-4 text-warning-600" />
                  <span className="text-sm text-secondary-600">Real-time Pricing</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-white/30 mt-8 pt-6 text-center">
            <p className="text-xs text-secondary-500">
              © 2024 CloudHelper Pro. Built for enterprises who demand transparency and compliance.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Layout 